import { Heart, Menu, X, ArrowRight, Mars, Venus, Trash2 } from 'lucide-react';
import { useQuestions } from './hooks/useQuestions';
import { useGameState } from './hooks/useGameState';
import { useTheme } from './context/ThemeContext';
import type { QuestionType, IntensityLevel, ThemeId } from './types';
import { useEffect, useRef } from 'react';
import './App.css';

function NeonParticles() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const colors = ['#ff2d8a', '#00d4ff', '#b44dff'];
    for (let i = 0; i < 30; i++) {
      const p = document.createElement('div');
      p.className = 'neon-particle';
      p.style.left = Math.random() * 100 + '%';
      p.style.animationDuration = (8 + Math.random() * 12) + 's';
      p.style.animationDelay = Math.random() * 10 + 's';
      const size = (2 + Math.random() * 4) + 'px';
      p.style.width = size;
      p.style.height = size;
      p.style.background = colors[Math.floor(Math.random() * 3)];
      container.appendChild(p);
    }
    return () => { container.innerHTML = ''; };
  }, []);

  return <div ref={containerRef} className="neon-particles" />;
}

function App() {
  const { isLoading, error, getRandomQuestion } = useQuestions();
  const {
    gameMode,
    selectedOption,
    currentContent,
    intensity,
    menuOpen,
    playerNames,
    playerGenders,
    currentPlayer,
    animation,
    disableTruth,
    disableDare,
    randomMode,
    setIntensity,
    setMenuOpen,
    setDisableTruth,
    setDisableDare,
    setRandomMode,
    handleOptionSelect,
    nextTurn,
    resetGame,
    addPlayer,
    updatePlayerName,
    toggleGender,
    removePlayer,
    startGame,
    skipQuestion
  } = useGameState();

  const { theme, setTheme } = useTheme();

  const handleSelectOption = (option: QuestionType) => {
    const currentGender = playerGenders[currentPlayer];
    const rawContent = getRandomQuestion(option, intensity, currentGender);
    handleOptionSelect(option, rawContent);
  };

  const handleRandomOption = () => {
    const options: QuestionType[] = [];
    if (!disableTruth) options.push('truth');
    if (!disableDare) options.push('dare');
    if (options.length === 0) return;
    const pick = options[Math.floor(Math.random() * options.length)];
    handleSelectOption(pick);
  };

  const handleSkip = () => {
    if (selectedOption) {
      const currentGender = playerGenders[currentPlayer];
      const rawContent = getRandomQuestion(selectedOption, intensity, currentGender);
      skipQuestion(rawContent);
    }
  };

  const autoPickOption = (): QuestionType | null => {
    if (disableTruth && !disableDare) return 'dare';
    if (!disableTruth && disableDare) return 'truth';
    if (randomMode) return Math.random() < 0.5 ? 'truth' : 'dare';
    return null;
  };

  const handleStartOrAutoSelect = () => {
    const pick = autoPickOption();
    if (pick) {
      startGame();
      setTimeout(() => {
        const currentGender = playerGenders[currentPlayer];
        const rawContent = getRandomQuestion(pick, intensity, currentGender);
        handleOptionSelect(pick, rawContent);
      }, 350);
    } else {
      startGame();
    }
  };

  const handleNextTurn = () => {
    const nextGender = playerGenders[(currentPlayer + 1) % playerNames.length];
    const pick = autoPickOption();
    if (pick) {
      nextTurn();
      setTimeout(() => {
        const rawContent = getRandomQuestion(pick, intensity, nextGender);
        handleOptionSelect(pick, rawContent);
      }, 350);
    } else {
      nextTurn();
    }
  };

  const intensityLevels: IntensityLevel[] = ['mild', 'moderate', 'spicy', 'extreme'];

  const themeOptions: { id: ThemeId; label: string }[] = [
    { id: 'neon', label: 'Neon Cards' },
    { id: 'velvet', label: 'Velvet Aurora' },
  ];

  return (
    <div className="gradient-bg text-white flex flex-col">
      {/* Theme-specific background effects */}
      {theme === 'neon' && <NeonParticles />}
      {theme === 'velvet' && (
        <>
          <div className="velvet-aurora" />
          <div className="velvet-aurora-mid" />
          <div className="velvet-noise" />
        </>
      )}

      {/* Header */}
      <header className="p-4 flex justify-between items-center relative z-10">
        <h1 className="text-2xl font-bold flex items-center">
          <span className={theme === 'neon'
            ? 'bg-gradient-to-r from-[#ff2d8a] to-[#b44dff] bg-clip-text text-transparent'
            : 'text-accent'
          }>
            Intimate
          </span>
          <Heart className="mx-2" size={20} style={{ color: 'var(--accent)' }} />
          <span className={theme === 'neon'
            ? 'bg-gradient-to-r from-[#ff2d8a] to-[#b44dff] bg-clip-text text-transparent'
            : 'text-accent'
          }>
            Games
          </span>
        </h1>
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="w-[42px] h-[42px] rounded-xl border border-white/10 flex items-center justify-center transition-all hover:border-[var(--accent)] hover:shadow-[0_0_20px_rgba(var(--accent),0.2)]"
          style={{ background: 'var(--card-bg)' }}
        >
          {menuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </header>

      {/* Menu Overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-50"
          style={{ background: theme === 'velvet' ? 'rgba(10,6,12,0.7)' : 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
          onClick={() => setMenuOpen(false)}
        >
          <div
            className="bg-settings fixed top-0 right-0 bottom-0 w-80 max-w-[85vw] p-6 flex flex-col overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-7">
              <h2 className="text-xl font-bold text-accent">Settings</h2>
              <button onClick={() => setMenuOpen(false)} className="text-white/60 hover:text-white">
                <X size={24} />
              </button>
            </div>

            {/* Theme */}
            <div className="mb-6">
              <h3 className="text-xs uppercase tracking-[3px] text-muted-theme mb-3 font-medium">Theme</h3>
              <div className="flex gap-2">
                {themeOptions.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTheme(t.id)}
                    className={`flex-1 py-2.5 px-3 rounded text-sm font-medium transition-all ${
                      theme === t.id ? 'bg-settings-active' : 'bg-settings-item hover:border-white/20'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Intensity Level */}
            <div className="mb-6">
              <h3 className="text-xs uppercase tracking-[3px] text-muted-theme mb-3 font-medium">Intensity</h3>
              <div className="flex gap-2">
                {intensityLevels.map((level) => (
                  <button
                    key={level}
                    onClick={() => setIntensity(level)}
                    className={`flex-1 py-2.5 px-3 rounded text-sm font-medium transition-all ${
                      intensity === level ? 'bg-settings-active' : 'bg-settings-item hover:border-white/20'
                    }`}
                  >
                    {level.charAt(0).toUpperCase() + level.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Question Type Toggles */}
            <div className="mb-6">
              <h3 className="text-xs uppercase tracking-[3px] text-muted-theme mb-3 font-medium">Options</h3>
              <div className="flex flex-col space-y-2">
                <div className="flex items-center justify-between py-2.5">
                  <span className="text-sm text-white/70">Disable Truth</span>
                  <button
                    onClick={() => {
                      if (!disableDare || disableTruth) {
                        setDisableTruth(!disableTruth);
                      }
                    }}
                    className={`w-11 h-6 rounded-full relative transition-colors ${
                      disableTruth ? 'bg-toggle-active' : 'bg-toggle-inactive'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded-full bg-white absolute top-0.5 left-0.5 transition-transform shadow-md ${
                        disableTruth ? 'translate-x-5' : ''
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between py-2.5">
                  <span className="text-sm text-white/70">Disable Dare</span>
                  <button
                    onClick={() => {
                      if (!disableTruth || disableDare) {
                        setDisableDare(!disableDare);
                      }
                    }}
                    className={`w-11 h-6 rounded-full relative transition-colors ${
                      disableDare ? 'bg-toggle-active' : 'bg-toggle-inactive'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded-full bg-white absolute top-0.5 left-0.5 transition-transform shadow-md ${
                        disableDare ? 'translate-x-5' : ''
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between py-2.5">
                  <span className="text-sm text-white/70">Random Truth or Dare</span>
                  <button
                    onClick={() => setRandomMode(!randomMode)}
                    className={`w-11 h-6 rounded-full relative transition-colors ${
                      randomMode ? 'bg-toggle-active' : 'bg-toggle-inactive'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded-full bg-white absolute top-0.5 left-0.5 transition-transform shadow-md ${
                        randomMode ? 'translate-x-5' : ''
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Players */}
            <div className="mb-6">
              <h3 className="text-xs uppercase tracking-[3px] text-muted-theme mb-3 font-medium">Players</h3>
              {playerNames.map((name, index) => (
                <div key={index} className="flex items-center gap-2 mb-2">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => updatePlayerName(index, e.target.value)}
                    className="input-themed p-2.5 rounded flex-1 text-sm"
                  />
                  <button
                    onClick={() => toggleGender(index)}
                    className={`w-[38px] h-[38px] flex items-center justify-center rounded transition-all ${
                      playerGenders[index] === 'male' ? 'bg-gender-male' : 'bg-gender-female'
                    } hover:opacity-80`}
                    title={`Click to change to ${
                      playerGenders[index] === 'male' ? 'Female' : 'Male'
                    }`}
                  >
                    {playerGenders[index] === 'male' ? (
                      <Mars size={18} />
                    ) : (
                      <Venus size={18} />
                    )}
                  </button>
                  {playerNames.length > 2 && (
                    <button
                      onClick={() => removePlayer(index)}
                      className="w-[38px] h-[38px] flex items-center justify-center rounded border border-white/10 text-white/40 hover:border-red-400 hover:text-red-400 transition-all"
                      title="Remove player"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={addPlayer}
                className="mt-1.5 w-full py-2.5 rounded border border-dashed text-sm transition-all hover:text-accent hover:border-[var(--accent)]"
                style={{ borderColor: 'var(--settings-item-border)', color: 'var(--text-muted)' }}
              >
                + Add Player
              </button>
            </div>

            <button
              onClick={resetGame}
              className="mt-auto py-3 rounded border text-sm font-medium transition-all hover:bg-red-500/10"
              style={{ borderColor: 'rgba(196, 82, 106, 0.3)', background: 'rgba(196, 82, 106, 0.06)', color: '#ff6b6b' }}
            >
              Reset Game
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center p-6 relative overflow-hidden z-10">
        {/* Loading Indicator */}
        {isLoading && (
          <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-40 bg-black bg-opacity-70 px-4 py-2 rounded-lg">
            <p className="text-sm">Loading questions...</p>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-40 bg-red-900 bg-opacity-90 px-4 py-2 rounded-lg">
            <p className="text-sm text-red-200">Error: {error}</p>
            <p className="text-xs text-red-300">Using local questions instead</p>
          </div>
        )}

        {/* Game Card */}
        <div
          className={`w-full max-w-md p-6 rounded-xl glass-card relative
            ${animation === 'fade-out' ? 'animate-fade-out' : ''}
            ${animation === 'fade-in' ? 'animate-fade-in' : ''}
            ${animation === 'slide-out' ? 'animate-slide-out' : ''}
            ${animation === 'slide-in' ? 'animate-slide-in' : ''}
          `}
        >
          {/* Start Screen */}
          {gameMode === 'start' && (
            <div className="text-center py-8">
              {theme === 'velvet' && (
                <p className="text-xs uppercase tracking-[5px] text-accent mb-4 opacity-70">
                  An intimate experience
                </p>
              )}
              <h2 className="text-3xl font-bold mb-2">
                <span className="block">Ready to</span>
                <span className={`block ${theme === 'neon'
                  ? 'bg-gradient-to-r from-[#ff2d8a] via-[#b44dff] to-[#00d4ff] bg-clip-text text-transparent'
                  : 'bg-gradient-to-r from-[#d4a574] via-[#c4526a] to-[#e8c9a0] bg-clip-text text-transparent italic'
                }`}>
                  Play?
                </span>
              </h2>
              {theme === 'velvet' && (
                <div className="flex items-center justify-center gap-3 my-6 opacity-30">
                  <div className="w-10 h-px bg-[#d4a574]" />
                  <div className="w-1.5 h-1.5 rounded-full bg-[#d4a574]" />
                  <div className="w-10 h-px bg-[#d4a574]" />
                </div>
              )}
              <p className={`mb-8 text-muted-theme ${theme === 'velvet' ? 'italic' : ''}`}>
                {theme === 'velvet'
                  ? 'Deepen your connection through truth & dare'
                  : 'Spice things up with truth or dare'}
              </p>
              <button
                onClick={handleStartOrAutoSelect}
                disabled={disableTruth && disableDare}
                className={`text-white py-4 px-12 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed btn-primary relative ${
                  theme === 'velvet'
                    ? 'rounded border border-[var(--accent)] bg-transparent text-accent uppercase tracking-[3px] text-sm hover:bg-gradient-to-r hover:from-[#d4a574] hover:to-[#9b2d4a] hover:text-white hover:border-transparent'
                    : 'rounded-full btn-gradient-primary'
                }`}
              >
                {theme === 'velvet' ? 'Begin' : 'Start Game'}
              </button>
              {disableTruth && disableDare && (
                <p className="mt-4 text-red-300 text-sm">
                  Please enable at least one question type in settings
                </p>
              )}
            </div>
          )}

          {/* Select Screen */}
          {gameMode === 'select' && (
            <div className="text-center py-4">
              <p className="text-xs uppercase tracking-[3px] text-muted-theme mb-2">
                {theme === 'velvet' ? 'Your turn' : "It's your turn"}
              </p>
              <h2 className={`text-2xl font-bold mb-8 ${theme === 'velvet' ? 'text-accent-light' : ''}`}>
                {playerNames[currentPlayer]}
              </h2>

              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => handleSelectOption('truth')}
                  disabled={disableTruth}
                  className={`card-truth text-white py-16 rounded-2xl text-xl font-bold transition-all selection-btn flex flex-col items-center justify-center gap-3 ${
                    disableTruth ? 'opacity-25 cursor-not-allowed pointer-events-none' : ''
                  }`}
                >
                  <span className="text-3xl">💎</span>
                  <span className="text-truth uppercase tracking-[2px]">Truth</span>
                </button>
                <button
                  onClick={() => handleSelectOption('dare')}
                  disabled={disableDare}
                  className={`card-dare text-white py-16 rounded-2xl text-xl font-bold transition-all selection-btn flex flex-col items-center justify-center gap-3 ${
                    disableDare ? 'opacity-25 cursor-not-allowed pointer-events-none' : ''
                  }`}
                >
                  <span className="text-3xl">🔥</span>
                  <span className="text-dare uppercase tracking-[2px]">Dare</span>
                </button>
              </div>

              {/* Random option */}
              {!disableTruth && !disableDare && (
                <button
                  onClick={handleRandomOption}
                  className="mt-4 w-full py-3.5 rounded-xl btn-gradient-primary text-white text-sm font-semibold uppercase tracking-[2px] transition-all btn-primary flex items-center justify-center gap-2"
                >
                  <span>🎲</span>
                  <span>Random</span>
                </button>
              )}
            </div>
          )}

          {/* Display Screen */}
          {gameMode === 'display' && (
            <div className="text-center py-4">
              <p className="text-xs uppercase tracking-[3px] text-muted-theme mb-2">
                {playerNames[currentPlayer]}
              </p>
              <div className={`inline-block px-5 py-1.5 rounded-sm text-xs font-bold uppercase tracking-[3px] mb-6 ${
                selectedOption === 'truth' ? 'badge-truth' : 'badge-dare'
              }`}>
                {selectedOption}
              </div>
              <h2 className={`text-xl font-normal mb-10 min-h-[100px] flex items-center justify-center leading-relaxed ${
                theme === 'velvet' ? 'italic text-white/90' : 'text-white/90'
              }`}>
                {currentContent}
              </h2>

              <div className="flex gap-3">
                <button
                  onClick={handleSkip}
                  className="flex-1 py-3.5 rounded btn-skip-themed flex items-center justify-center text-sm font-semibold transition-all uppercase tracking-wider"
                >
                  Skip
                </button>
                <button
                  onClick={handleNextTurn}
                  className="flex-1 py-3.5 rounded btn-gradient-primary text-white flex items-center justify-center text-sm font-semibold transition-all btn-primary uppercase tracking-wider"
                >
                  Next
                  <ArrowRight size={16} className="ml-2" />
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className={`p-4 text-center text-sm relative z-10 ${
        theme === 'velvet' ? 'italic text-white/20' : 'text-white/25'
      }`}>
        Always respect boundaries and consent.
      </footer>
    </div>
  );
}

export default App;
